import React, { useEffect } from 'react';
import { ArrowRight, Check, LayoutDashboard, Shield, Sparkles } from 'lucide-react';
import { resolveLandingOrigin, resolvePanelLoginUrl, resolvePanelRegisterUrl } from '../utils/booking';
import { applySeo } from '../utils/seo';
import { whyFreePage } from '../data/conversionPages';
import '../styles/landing.css';
import '../styles/conversion-pages.css';

export default function WhyFreePage() {
  const panelRegisterUrl = resolvePanelRegisterUrl();
  const panelLoginUrl = resolvePanelLoginUrl();

  useEffect(() => {
    const landingOrigin = resolveLandingOrigin();
    const pageUrl = `${landingOrigin}${whyFreePage.path}`;

    applySeo({
      title: whyFreePage.title,
      description: whyFreePage.description,
      keywords: whyFreePage.keywords,
      robots: 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
      canonical: pageUrl,
      ogTitle: whyFreePage.title,
      ogDescription: whyFreePage.description,
      ogUrl: pageUrl,
      themeColor: '#f8fafb',
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: whyFreePage.title,
        description: whyFreePage.description,
        url: pageUrl,
        inLanguage: 'pl-PL',
      },
    });
  }, []);

  return (
    <div className="landing-page conversion-page">
      <nav className="landing-nav conversion-nav" aria-label="Nawigacja główna">
        <a href="/" className="landing-nav-logo" aria-label="ClientWay - strona główna">
          <span className="landing-logo-icon"><LayoutDashboard size={18} /></span>
          <span className="landing-logo-text">Client<span>Way</span></span>
        </a>

        <div className="industry-nav-links">
          <a href="/">Strona główna</a>
          <a href="/fryzjer">Branże</a>
          <a href={panelLoginUrl}>Zaloguj się</a>
          <a href={panelRegisterUrl} className="industry-nav-cta">Zacznij za darmo</a>
        </div>
      </nav>

      <div className="conversion-sticky-bar">
        <div className="landing-container conversion-sticky-inner">
          <div className="conversion-sticky-copy">
            <strong>Zmień płatny kalendarz na ClientWay w 2 minuty.</strong>
            <span>0 zł abonamentu, 0 zł prowizji, 0 zł za SMS.</span>
          </div>
          <a href={panelRegisterUrl} className="conversion-sticky-cta">
            Załóż darmowe konto
            <ArrowRight size={14} />
          </a>
        </div>
      </div>

      <section className="conversion-hero">
        <div className="landing-container conversion-hero-inner">
          <div className="landing-badge">
            <Sparkles size={12} />
            Manifest wolności
          </div>
          <h1>{whyFreePage.h1}</h1>
          <p className="conversion-hero-lead">{whyFreePage.heroLead}</p>
          <p className="conversion-hero-intro">{whyFreePage.intro}</p>
          <div className="landing-cta-buttons">
            <a href={panelRegisterUrl} className="landing-btn-cta-primary">
              Rozpocznij za darmo
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      <section className="landing-section">
        <div className="landing-container conversion-trust-grid">
          {whyFreePage.benefits.map((benefit) => (
            <div key={benefit} className="conversion-trust-pill">
              <Check size={14} />
              {benefit}
            </div>
          ))}
        </div>
      </section>

      <section className="landing-section landing-section-muted">
        <div className="landing-container">
          <div className="landing-section-header">
            <p className="landing-section-label">Model biznesowy</p>
            <h2>Dlaczego ClientWay jest za darmo?</h2>
            <p>Bo narzędzie ma być pomocą dla usługodawców, a nie kolejnym pośrednikiem od ich pracy.</p>
          </div>

          <div className="conversion-feature-grid">
            {whyFreePage.sections.map((section) => (
              <article key={section.title} className="conversion-feature-card">
                <h2>{section.title}</h2>
                <p>{section.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section">
        <div className="landing-container">
          <div className="landing-section-header">
            <p className="landing-section-label">Porównanie kosztów</p>
            <h2>{whyFreePage.comparisonTitle}</h2>
            <p>{whyFreePage.comparisonLead}</p>
          </div>

          <div className="conversion-comparison-table" role="table" aria-label="Porównanie kosztów">
            <div className="conversion-comparison-row conversion-comparison-head" role="row">
              <span role="columnheader">Element</span>
              <span role="columnheader">ClientWay</span>
              <span role="columnheader">Typowy płatny system</span>
            </div>
            {whyFreePage.comparisonRows.map((row) => (
              <div key={row.label} className="conversion-comparison-row" role="row">
                <span role="cell">{row.label}</span>
                <span role="cell">{row.clientWay}</span>
                <span role="cell">{row.paidSystem}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section landing-section-muted">
        <div className="landing-container">
          <div className="landing-section-header">
            <p className="landing-section-label">Dlaczego nie ma haczyka?</p>
            <h2>{whyFreePage.noCatchTitle}</h2>
            <p>{whyFreePage.noCatchLead}</p>
          </div>

          <div className="conversion-feature-grid">
            {whyFreePage.noCatchPoints.map((point) => (
              <article key={point} className="conversion-feature-card">
                <p>{point}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section">
        <div className="landing-container">
          <div className="landing-section-header">
            <p className="landing-section-label">Manifest</p>
            <h2>Dlaczego darmowy model ma sens?</h2>
            <p>To proste zdanie wyjaśnia, po co istnieje ClientWay i czemu nie ma tu ukrytych kosztów.</p>
          </div>

          <div className="conversion-manifest-grid">
            {whyFreePage.manifestNotes.map((note) => (
              <article key={note} className="conversion-manifest-card">
                <p>{note}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section">
        <div className="landing-container">
          <div className="landing-section-header">
            <p className="landing-section-label">Case Study</p>
            <h2>Jak wygląda przesiadka na darmowy kalendarz?</h2>
            <p>Trzy krótkie scenariusze pokazujące, co zmienia się po przejściu na ClientWay.</p>
          </div>

          <div className="conversion-case-grid">
            {whyFreePage.caseStudies.map((item) => (
              <article key={item.title} className="conversion-case-card">
                <div className="conversion-case-label">{item.title}</div>
                <blockquote>{item.quote}</blockquote>
                <p>{item.result}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section landing-section-muted">
        <div className="landing-container">
          <div className="landing-section-header">
            <p className="landing-section-label">Miasta</p>
            <h2>Strony rezerwacji dla wybranych miast</h2>
            <p>Możesz wejść w lokalny ruch bez ręcznego tworzenia każdej strony od zera.</p>
          </div>

          <div className="conversion-city-grid">
            {whyFreePage.cities.map((citySlug) => (
              <a key={citySlug} href={`/rezerwacje/${citySlug}`} className="conversion-city-chip">
                {citySlug.charAt(0).toUpperCase() + citySlug.slice(1)}
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section">
        <div className="landing-container conversion-faq-wrap">
          <div className="landing-section-header">
            <p className="landing-section-label">FAQ</p>
            <h2>Najczęstsze pytania o darmowy model</h2>
            <p>To sekcja zaufania, bo ludzie muszą wiedzieć, że „za darmo” naprawdę znaczy za darmo.</p>
          </div>

          <div className="conversion-faq-grid">
            {whyFreePage.faqs.map((faq) => (
              <article key={faq.question} className="conversion-faq-card">
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-final-cta">
        <div className="landing-container landing-cta-inner">
          <div className="landing-badge landing-badge-cta">
            <Shield size={12} />
            0 zł dla usługodawców
          </div>
          <h2>Wejdź do ClientWay i zostaw pieniądze w swojej kieszeni.</h2>
          <p>Start jest prosty, a model biznesowy jest uczciwy: bez abonamentu, bez prowizji i bez dopłat za SMS.</p>
          <div className="landing-cta-buttons">
            <a href={panelRegisterUrl} className="landing-btn-cta-primary">
              Rozpocznij za darmo
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
